import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const boolVal = (value) => Boolean(value);

const NewAppDialog = ({
  open,
  form,
  preview,
  error,
  previewLoading,
  createLoading,
  onClose,
  onChange,
  onPreview,
  onCreate,
}) => {
  const plannedFiles = Array.isArray(preview?.planned_files) ? preview.planned_files : [];
  const plannedCommands = Array.isArray(preview?.planned_commands) ? preview.planned_commands : [];
  const warnings = Array.isArray(preview?.warnings) ? preview.warnings : [];
  const conflicts = Array.isArray(preview?.conflicts) ? preview.conflicts : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create new app</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            autoFocus
            label="App name"
            value={form?.app_name || ""}
            onChange={(e) => onChange("app_name", e.target.value)}
            fullWidth
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Project ID"
              value={form?.project_id || ""}
              onChange={(e) => onChange("project_id", e.target.value)}
              fullWidth
              helperText="Used in /opt/apps/<project_id>"
            />
            <TextField
              label="App port"
              type="number"
              value={form?.backend_port ?? ""}
              onChange={(e) => onChange("backend_port", e.target.value)}
              sx={{ minWidth: 160 }}
              helperText="Public URL will open on 149.28.176.255:<port>. When nginx is enabled, an internal API port is derived automatically."
            />
          </Stack>

          <TextField
            label="Base path"
            value={form?.base_path || ""}
            onChange={(e) => onChange("base_path", e.target.value)}
            fullWidth
          />

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Backend root path"
              value={form?.backend_root_path || ""}
              onChange={(e) => onChange("backend_root_path", e.target.value)}
              fullWidth
              helperText="Use the app root, e.g. /opt/apps/<project_id>"
            />
            <TextField
              label="Frontend root path"
              value={form?.frontend_root_path || ""}
              onChange={(e) => onChange("frontend_root_path", e.target.value)}
              fullWidth
              helperText="Frontend stays under <app_root>/frontend"
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Starter module"
              value={form?.starter_module || ""}
              onChange={(e) => onChange("starter_module", e.target.value)}
              fullWidth
              helperText="Creates modular backend/frontend starter folders under the canonical scaffold"
            />
            <TextField
              label="Server name"
              value={form?.server_name || "_"}
              onChange={(e) => onChange("server_name", e.target.value)}
              fullWidth
              helperText="Optional nginx server_name. The generated open URL uses the fixed public IP."
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={boolVal(form?.enable_systemd)}
                  onChange={(e) => onChange("enable_systemd", e.target.checked)}
                />
              }
              label="Create systemd service"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={boolVal(form?.enable_nginx)}
                  onChange={(e) => onChange("enable_nginx", e.target.checked)}
                />
              }
              label="Create nginx config"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={boolVal(form?.overwrite_existing)}
                  onChange={(e) => onChange("overwrite_existing", e.target.checked)}
                />
              }
              label="Allow overwrite of generator-owned files"
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={boolVal(form?.auto_build_frontend)}
                  onChange={(e) => onChange("auto_build_frontend", e.target.checked)}
                />
              }
              label="Run npm install + build automatically"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={boolVal(form?.open_firewall)}
                  onChange={(e) => onChange("open_firewall", e.target.checked)}
                />
              }
              label="Apply firewall rule automatically"
            />
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {preview ? (
            <Box sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Preview
              </Typography>

              {conflicts.length > 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {conflicts.length} conflict(s) detected. Creation is blocked until these are resolved or overwrite is enabled.
                </Alert>
              ) : null}

              {warnings.length > 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {warnings.join(" ")}
                </Alert>
              ) : null}

              {preview?.app_url ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Expected URL: {preview.app_url}
                </Typography>
              ) : null}

              <Typography variant="body2" sx={{ mb: 1 }}>
                Planned files: {plannedFiles.length}
              </Typography>

              {plannedCommands.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Planned commands: {plannedCommands.length}
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 140,
                      overflow: "auto",
                      bgcolor: "grey.50",
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 1,
                      p: 1.5,
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                  >
                    {plannedCommands.map((cmd) => (
                      <Typography key={cmd} variant="body2" sx={{ fontFamily: "inherit" }}>
                        {cmd}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : null}

              <Box
                sx={{
                  maxHeight: 220,
                  overflow: "auto",
                  bgcolor: "grey.50",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1,
                  p: 1.5,
                  fontFamily: "monospace",
                  fontSize: 12,
                }}
              >
                {plannedFiles.length > 0 ? plannedFiles.map((path) => (
                  <Typography key={path} variant="body2" sx={{ fontFamily: "inherit" }}>
                    {path}
                  </Typography>
                )) : (
                  <Typography variant="body2" color="text.secondary">
                    No preview files returned.
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={previewLoading || createLoading}>
          Cancel
        </Button>
        <Button onClick={onPreview} disabled={previewLoading || createLoading} variant="outlined">
          {previewLoading ? "Previewing..." : "Preview"}
        </Button>
        <Button onClick={onCreate} disabled={createLoading || previewLoading} variant="contained">
          {createLoading ? "Creating..." : "Create app"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewAppDialog;
